import { ChatInput } from "@/chat/components/chat/ChatInput";
import { ChatMessages } from "@/chat/components/chat/ChatMessages";
import { VoiceCallBar, VoiceRing } from "@/chat/components/chat/VoiceMode";
import { useWebAudio } from "@/chat/hooks/use-web-audio";
import { CustomerTile } from "@/components/molecules/customer-tile";
import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useUser } from "@/features/auth/hooks/use-user";
import { useClientLinks } from "@/features/clients/hooks/use-scoped-client";
import { useReadOnly } from "@/features/utils/api-scope";
import {
  useSendSessionMessage,
  useSession,
  type SessionMessage,
} from "@/features/sessions";
import { shownTags } from "@/features/sessions/classification";
import { SessionsAPI } from "@/features/sessions/api/sessions";
import { downloadBlob } from "@/features/utils/blob";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { websocketBus } from "@/features/websockets";
import {
  ArrowLeftIcon,
  ChatsCircleIcon,
  ClockIcon,
  CodeIcon,
  DotsThreeVerticalIcon,
  DownloadSimpleIcon,
  RobotIcon,
  SpeakerHighIcon,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MetaIcon,
  ResultBadge,
  TagBadge,
  formatDuration,
  ChannelBadge,
} from "./conversation-meta";
import { AdminComposer } from "./admin-composer";
import { OperatorComposer } from "./operator-composer";
import { SystemPromptDialog } from "./system-prompt-dialog";
import { VisitorMeta } from "./visitor-meta";

/**
 * The right pane: one conversation, in full.
 *
 * Everything that used to be the session page lives here — the websocket, the
 * optimistic messages, the web-audio leg, the SYSADMIN exports — because the
 * page around it is now a list *and* a conversation, and only the conversation
 * half depends on the session.
 */
export function ConversationView({
  sessionId,
  onBack,
  hideHeader,
  composer = "admin",
}: {
  sessionId: string;
  onBack?: () => void;
  /**
   * What sits under the thread. `admin` (the conversations history): nothing at
   * all, except for a SYSADMIN, who gets the folded `AdminComposer`. `chat` (the
   * agent test panel): the member is the customer there, and types as one.
   */
  composer?: "admin" | "chat";
  /**
   * For a host that says who the conversation is with itself — the agent test
   * panel, where the header would only read "unknown visitor, untitled".
   */
  hideHeader?: boolean;
}) {
  const { t } = useTranslation("calls");
  const navigate = useNavigate();
  const [textInput, setTextInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // The conversation we have already jumped to the end of, so re-opening it
  // does not animate and a live message does not jump.
  const landedOnRef = useRef<string | undefined>(undefined);
  const [optimisticMessages, setOptimisticMessages] = useState<
    SessionMessage[]
  >([]);
  const [streamingChunk, setStreamingChunk] = useState<string>("");
  const [removedTimestamps, setRemovedTimestamps] = useState<Set<number>>(
    new Set(),
  );
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const { data: agents } = useAgents();
  const { data: user } = useUser();
  const links = useClientLinks();
  // The client's calls on its file in the platform back-office are read only,
  // for the read-only staff account and a SYSADMIN alike: no composer there.
  const readOnly = useReadOnly();
  // Platform staff: raw audio, CSV export and tool internals are for SYSADMINs only.
  const isSysadmin = user?.role === "SYSADMIN";

  const { data: session, isLoading, isError, refetch } = useSession(sessionId);
  const sendMessage = useSendSessionMessage();

  // Web audio (mic) — only for chat/web sessions, not archived phone calls, and
  // only where the member is the one talking to the agent (the test panel).
  const canUseAudio =
    composer === "chat" && !!session && session.medium !== "call";
  const {
    status: audioStatus,
    error: audioError,
    toggle: toggleAudio,
    stop: stopAudio,
    sendText: sendAudioText,
    agentAnalyserRef,
    micAnalyserRef,
    phaseRef,
  } = useWebAudio(
    canUseAudio && session
      ? `/api/sessions/v1/${session.client_id}/sessions/${sessionId}`
      : undefined,
  );
  const onCall =
    canUseAudio && (audioStatus === "live" || audioStatus === "connecting");
  useEffect(() => {
    if (audioError) toast.error(audioError);
  }, [audioError]);
  const [exporting, setExporting] = useState(false);
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [systemPromptOpen, setSystemPromptOpen] = useState(false);

  // Download the source call recording (SYSADMIN only) — or the raw recording of
  // what a team member said on the phone (after a transfer, or a handset's own
  // call), as our Asterisk made it, before it was appended to the call's audio.
  const handleDownloadAudio = async (kind: "call" | "team" = "call") => {
    if (!session || !sessionId) return;
    setDownloadingAudio(true);
    try {
      const blob =
        kind === "team"
          ? await SessionsAPI.downloadTeamRecording(
              session.client_id,
              sessionId,
            )
          : await SessionsAPI.downloadRecording(session.client_id, sessionId);
      downloadBlob(blob, `${kind}_${sessionId}.wav`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("session.audio_error"),
      );
    } finally {
      setDownloadingAudio(false);
    }
  };

  // Download the whole conversation as a readable, silence-annotated CSV.
  const handleExportCsv = async () => {
    if (!session || !sessionId) return;
    setExporting(true);
    try {
      const blob = await SessionsAPI.exportCsv(session.client_id, sessionId);
      downloadBlob(blob, `conversation_${sessionId}.csv`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("session.export_error"),
      );
    } finally {
      setExporting(false);
    }
  };

  // Listen to websocket for real-time messages and chunks
  useEffect(() => {
    if (!sessionId) return;

    const room = `client/sessions/${sessionId}/messages`;

    // Join the websocket room
    websocketBus.emit("join", null, { room });

    const handleWebsocketEvent = (
      data:
        | SessionMessage
        | { type: "chunk"; value: string }
        | { type: "thinking" }
        | { _remove?: number[]; _removed?: string[] },
    ) => {
      // Handle message removal — a turn the caller superseded mid-sentence is deleted
      // server-side, and its bubbles have to go with it. `_removed` carries row ids;
      // `_remove` is the older timestamp form, still accepted so a client ahead of the
      // backend keeps working.
      if (
        ("_remove" in data && Array.isArray(data._remove)) ||
        ("_removed" in data && Array.isArray(data._removed))
      ) {
        const timestamps = data._remove || [];
        const ids = data._removed || [];
        if (timestamps.length)
          setRemovedTimestamps((prev) => new Set([...prev, ...timestamps]));
        if (ids.length) setRemovedIds((prev) => new Set([...prev, ...ids]));
        setOptimisticMessages((prev) =>
          prev.filter(
            (m) =>
              !timestamps.includes(m.timestamp) &&
              !(m.id && ids.includes(m.id)),
          ),
        );
        return;
      }

      // The model started to think: nothing to show here, and not a message.
      if ("type" in data && data.type === "thinking") return;

      // Handle streaming chunks
      if ("type" in data && data.type === "chunk") {
        setStreamingChunk((prev) => prev + data.value);
        return;
      }

      // Handle complete messages
      const message = data as SessionMessage;

      // Clear streaming chunk when a complete message arrives
      setStreamingChunk("");

      // Add message optimistically
      setOptimisticMessages((prev) => {
        // Check if message already exists by timestamp
        const exists = prev.some((m) => m.timestamp === message.timestamp);
        if (exists) return prev;
        return [...prev, message];
      });
    };

    websocketBus.on(room, handleWebsocketEvent);

    return () => {
      websocketBus.off(room);
      websocketBus.emit("leave", null, { room });
      setStreamingChunk("");
      setOptimisticMessages([]);
      setRemovedTimestamps(new Set());
      setRemovedIds(new Set());
    };
  }, [sessionId]);

  // Reconcile optimistic messages with actual session data
  useEffect(() => {
    if (!session?.messages) return;

    const serverTimestamps = new Set(session.messages.map((m) => m.timestamp));

    // Remove optimistic messages that are now in the server data
    setOptimisticMessages((prev) =>
      prev.filter((m) => !serverTimestamps.has(m.timestamp)),
    );
  }, [session?.messages]);

  // Combine all messages from previous sessions and current session
  const allMessagesWithSession: Array<
    SessionMessage & { sessionId: string; agentId: string }
  > = [];

  // Add messages from previous sessions (oldest first)
  if (session?.previous_sessions) {
    for (let i = session.previous_sessions.length - 1; i >= 0; i--) {
      const prevSession = session.previous_sessions[i];
      prevSession.messages.forEach((msg) => {
        allMessagesWithSession.push({
          ...msg,
          sessionId: prevSession.id,
          agentId: prevSession.agent_id,
        });
      });
    }
  }

  // Add messages from current session
  if (session?.messages) {
    session.messages.forEach((msg) => {
      allMessagesWithSession.push({
        ...msg,
        sessionId: session.id,
        agentId: session.agent_id,
      });
    });
  }

  // Add optimistic messages
  optimisticMessages.forEach((msg) => {
    allMessagesWithSession.push({
      ...msg,
      sessionId: session?.id || "",
      agentId: session?.agent_id || "",
    });
  });

  // Filter and sort all messages
  const allMessages = allMessagesWithSession
    .filter(
      (message) =>
        !removedTimestamps.has(message.timestamp) &&
        !(message.id && removedIds.has(message.id)),
    )
    .sort((a, b) => a.timestamp - b.timestamp)
    .filter((message) => {
      // Keep tool messages if they have a tool name
      if (message.type === "tool") {
        return message?.tool?.name?.trim();
      }
      // For regular messages, check if value has content
      return message.value?.trim();
    });

  // Auto-scroll to the end of the thread.
  //
  // `block: "end"` is load-bearing: the end marker is an empty div, and
  // scrolling it to the *start* of the viewport stops several hundred pixels
  // short of the last message on a long call. Opening a conversation jumps
  // there outright — a reader picking a call wants its outcome, not its
  // greeting — while a message arriving under their eyes slides in.
  // Twice, because the header grows when the customer tile lands.
  useEffect(() => {
    const opening = landedOnRef.current !== sessionId;
    const scroll = () =>
      messagesEndRef.current?.scrollIntoView({
        behavior: opening ? "auto" : "smooth",
        block: "end",
      });
    const soon = setTimeout(scroll, 100);
    const settled = setTimeout(() => {
      scroll();
      landedOnRef.current = sessionId;
    }, 600);
    return () => {
      clearTimeout(soon);
      clearTimeout(settled);
    };
  }, [sessionId, allMessages.length, streamingChunk, sendMessage.isPending]);

  const handleSendText = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim() || !sessionId) return;

    try {
      await sendMessage.mutateAsync({ sessionId, message: textInput });
      setTextInput("");
      // Note: Query invalidation is handled by useSendSessionMessage's onSuccess
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.messageError"),
      );
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-20 w-2/3" />
        <Skeleton className="ml-auto h-16 w-1/2" />
        <Skeleton className="h-24 w-3/4" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState compact onRetry={refetch} className="h-full" />;
  }

  if (!session) {
    return (
      <EmptyState
        compact
        icon={ChatsCircleIcon}
        title={t("session.notFound")}
        description={t("session.notFoundDescription")}
        className="h-full"
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!hideHeader && (
        <ConversationHeader
          session={session}
          agents={agents}
          onBack={onBack}
          isSysadmin={isSysadmin}
          exporting={exporting}
          downloadingAudio={downloadingAudio}
          onExportCsv={handleExportCsv}
          onDownloadAudio={() => handleDownloadAudio("call")}
          onDownloadTeamAudio={() => handleDownloadAudio("team")}
          onOpenSystemPrompt={() => setSystemPromptOpen(true)}
        />
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <ChatMessages
          allMessages={allMessages}
          streamingChunk={streamingChunk}
          messagesEndRef={messagesEndRef}
          isDebugMode={isSysadmin}
          density="compact"
          showToolOutcome
          showStaffMessages={isSysadmin}
          session={{
            ...session,
            agents: (agents || [])
              .map((a) => [a.id, a.name])
              .reduce(
                (acc, [id, name]) => {
                  acc[id] = { name };
                  return acc;
                },
                {} as Record<string, { name: string }>,
              ),
          }}
          onGoToLastTransfer={
            session.last_transfer_session_id
              ? () => navigate(links.call(session.last_transfer_session_id!))
              : undefined
          }
          topSpacerClass={onCall ? "h-52" : undefined}
        />
        {onCall && (
          <VoiceRing
            agentAnalyserRef={agentAnalyserRef}
            micAnalyserRef={micAnalyserRef}
            phaseRef={phaseRef}
          />
        )}
      </div>

      {onCall ? (
        <VoiceCallBar
          onHangup={stopAudio}
          connecting={audioStatus === "connecting"}
          onSendText={sendAudioText}
        />
      ) : composer === "chat" ? (
        <ChatInput
          textInput={textInput}
          setTextInput={setTextInput}
          onSubmit={handleSendText}
          disabled={sendMessage.isPending}
          webCallEnabled={canUseAudio}
          onStartAudio={canUseAudio ? toggleAudio : undefined}
          hidePoweredBy
        />
      ) : (
        !readOnly && (
          <>
            {/* The team answering on WhatsApp / Messenger / Instagram. */}
            <OperatorComposer session={session} />
            {isSysadmin && (
              <AdminComposer
                session={session}
                // Unfolding it shortens the thread from below: keep its end in view.
                onToggle={() =>
                  messagesEndRef.current?.scrollIntoView({ block: "end" })
                }
              />
            )}
          </>
        )
      )}

      {isSysadmin && (
        <SystemPromptDialog
          clientId={session.client_id}
          sessionId={sessionId}
          open={systemPromptOpen}
          onOpenChange={setSystemPromptOpen}
        />
      )}
    </div>
  );
}

/**
 * Who the conversation was with, what it was about, and the metadata the list
 * could only afford to draw as icons — spelled out here, where there is room.
 */
function ConversationHeader({
  session,
  agents,
  onBack,
  isSysadmin,
  exporting,
  downloadingAudio,
  onExportCsv,
  onDownloadAudio,
  onDownloadTeamAudio,
  onOpenSystemPrompt,
}: {
  session: NonNullable<ReturnType<typeof useSession>["data"]>;
  agents?: { id: string; name: string }[];
  onBack?: () => void;
  isSysadmin: boolean;
  exporting: boolean;
  downloadingAudio: boolean;
  onExportCsv: () => void;
  onDownloadAudio: () => void;
  onDownloadTeamAudio: () => void;
  onOpenSystemPrompt: () => void;
}) {
  const { t } = useTranslation("calls");
  const locale = useDateFnsLocale();
  const agentName = agents?.find((a) => a.id === session.agent_id)?.name;
  const contact = session.context?.phone_number || session.context?.email;

  // Three lines: what the conversation was about, who it was with, how it went.
  // It used to take six, with the customer's tile and its contacts on three.
  return (
    <div className="shrink-0 space-y-3 border-b px-5 pb-4 pt-4">
      <div className="flex items-start gap-2">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 md:hidden"
            onClick={onBack}
            aria-label={t("back")}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <h2 className="text-lg font-semibold leading-snug tracking-tight">
            {session.name || t("list.untitled")}
          </h2>
          {session.customer_id ? (
            <CustomerTile
              customerId={session.customer_id}
              fallback={contact || t("list.unknownCustomer")}
              variant="inline"
              clickable
            />
          ) : (
            <div className="text-sm font-medium">
              {contact || t("table.unknown")}
            </div>
          )}
        </div>
        {isSysadmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="-mr-2">
                <DotsThreeVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onOpenSystemPrompt}>
                <CodeIcon className="h-4 w-4" />
                {t("session.system_prompt")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDownloadAudio}
                disabled={downloadingAudio}
              >
                <SpeakerHighIcon className="h-4 w-4" />
                {t("session.download_audio")}
              </DropdownMenuItem>
              {session.context?.human_recording && (
                <DropdownMenuItem
                  onClick={onDownloadTeamAudio}
                  disabled={downloadingAudio}
                >
                  <SpeakerHighIcon className="h-4 w-4" />
                  {t("session.download_team_audio")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onExportCsv} disabled={exporting}>
                <DownloadSimpleIcon className="h-4 w-4" />
                {t("session.export_csv")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <ChannelBadge session={session} />
        <ResultBadge session={session} />
        {shownTags(session.tags).map((tag) => (
          <TagBadge key={tag} tag={tag} />
        ))}
        <span className="ml-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          {session.duration > 0 && (
            <MetaIcon
              icon={ClockIcon}
              label={t("table.duration")}
              value={formatDuration(session.duration / 1000)}
            />
          )}
          {agentName && (
            <MetaIcon
              icon={RobotIcon}
              label={t("table.agent")}
              value={agentName}
            />
          )}
          <span className="text-xs text-muted-foreground">
            {format(new Date(session.created_at), "PPp", { locale })}
          </span>
        </span>
      </div>

      <VisitorMeta context={session.context} />
    </div>
  );
}
